import { setDay } from 'date-fns';
import { useMemo } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { formatWeekdayShort } from '../../../lib/date-helpers.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import {
    DEFAULT_INTERVAL,
    generateRecurrenceString,
    getDefaultRecurrence,
    parseRecurrenceString,
} from '../../../utils/rrule.ts';

type RRuleInputProps = {
    startDate: string;
    value?: string;
    onChange: (value: string) => void;
    disabled?: boolean;
};

const INTERVAL_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);
const WEEKDAYS_OPTIONS = Array.from({ length: 7 }, (_, i) => (i < 6 ? i + 1 : 0));

export const RRuleInput = ({ value, onChange, startDate, disabled = false }: RRuleInputProps) => {
    const { t, locale } = useLocale('events');

    const {
        weekdays: selectedDays,
        interval,
        excludedDates,
    } = useMemo(() => {
        const defaultWeekday = new Date(startDate).getDay();
        const options = parseRecurrenceString(value ?? getDefaultRecurrence(), startDate);

        if (!options) {
            return {
                weekdays: [defaultWeekday],
                interval: DEFAULT_INTERVAL,
                excludedDates: [],
            };
        }

        return {
            weekdays: [defaultWeekday],
            ...options,
        };
    }, [value, startDate]);

    const handleIntervalChange = (newValue: number) => {
        onChange(
            generateRecurrenceString(
                {
                    interval: newValue,
                    weekdays: selectedDays,
                    excludedDates,
                },
                startDate,
            ),
        );
    };

    const handleWeekdayToggle = (day: number) => {
        const selectedValues = new Set(selectedDays);

        if (selectedValues.has(day)) {
            selectedValues.delete(day);
        } else {
            selectedValues.add(day);
        }

        onChange(
            generateRecurrenceString(
                {
                    weekdays: [...selectedValues],
                    interval,
                    excludedDates,
                },
                startDate,
            ),
        );
    };

    return (
        <div>
            <div className="flex gap-3 flex-col sm:flex-row">
                <div className="flex flex-col gap-1.5">
                    <div>
                        <Text color="neutral-950">{t('events__form_recurrence_repeat_every')}</Text>
                    </div>
                    <div className="flex items-center gap-3">
                        <Select
                            value={interval.toString()}
                            onValueChange={(newValue) => {
                                const newInterval = Number.parseInt(newValue, 10);
                                handleIntervalChange(
                                    Number.isNaN(newInterval) ? DEFAULT_INTERVAL : newInterval,
                                );
                            }}
                            disabled={disabled}
                        >
                            <SelectTrigger className="grow-0 w-auto min-w-24">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {INTERVAL_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option.toString(10)}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Text color="neutral-950">{t('events__form_recurrence_weeks')}</Text>
                    </div>
                </div>
                <div className="flex flex-col gap-1.5">
                    <div>
                        <Text color="neutral-950">{t('events__form_recurrence_on')}</Text>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {WEEKDAYS_OPTIONS.map((weekDay) => {
                            const isSelected = selectedDays.includes(weekDay);

                            return (
                                <Button
                                    key={weekDay}
                                    variant="outline"
                                    size="icon-s"
                                    className={cn('text-sm', {
                                        'bg-blue-50': isSelected,
                                    })}
                                    rounded={true}
                                    selected={isSelected}
                                    onClick={() => {
                                        handleWeekdayToggle(weekDay);
                                    }}
                                    disabled={disabled}
                                >
                                    {formatWeekdayShort(setDay(new Date(), weekDay), locale).charAt(0)}
                                </Button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
